import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  RangeSliderWrapper,
  StyledTrack,
  StyledRange,
  StyledThumb,
} from "./styles";

interface RangeSliderProps {
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onInput: (value: [number, number]) => void;
  className?: string;
}

// Debounce simples
const debounce = (fn: any, delay: number) => {
  let timer: any;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

export const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  step = 1,
  value,
  onInput,
  className,
}) => {
  const [localValue, setLocalValue] = useState<[number, number]>(value);
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingThumbRef = useRef<0 | 1 | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (draggingThumbRef.current === null) {
      setLocalValue(value);
    }
  }, [value]);

  const debouncedOnInput = useCallback(
    debounce((updated: [number, number]) => {
      onInput(updated);
    }, 10),
    [onInput],
  );

  const updateFromClientX = (clientX: number) => {
    if (!trackRef.current || draggingThumbRef.current === null) return;

    const rect = trackRef.current.getBoundingClientRect();
    const percentage = (clientX - rect.left) / rect.width;
    const raw = min + percentage * (max - min);
    const stepped = Math.round(raw / step) * step;
    const clamped = Math.min(Math.max(stepped, min), max);

    const updated = [...localValue] as [number, number];

    if (draggingThumbRef.current === 0) {
      updated[0] = Math.min(clamped, updated[1] - step);
    } else {
      updated[1] = Math.max(clamped, updated[0] + step);
    }

    if (!animationFrameRef.current) {
      animationFrameRef.current = requestAnimationFrame(() => {
        setLocalValue(updated);
        debouncedOnInput(updated);
        animationFrameRef.current = null;
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => updateFromClientX(e.clientX);
  const handleTouchMove = (e: TouchEvent) => {
    if (e.cancelable) e.preventDefault(); // Impede scroll vertical
    updateFromClientX(e.touches[0].clientX);
  };

  const stopDrag = () => {
    draggingThumbRef.current = null;

    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", stopDrag);
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", stopDrag);

    // ⚠️ Apenas remove o bloqueio temporário de scroll
    document.body.removeEventListener("touchmove", handleTouchMove);
  };

  const startDrag = (thumbIndex: 0 | 1) => {
    draggingThumbRef.current = thumbIndex;

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", stopDrag);

    // ⚠️ Evita scroll do body durante drag, sem alterar estilo
    document.body.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
  };

  return (
    <RangeSliderWrapper className={className} ref={trackRef}>
      <StyledTrack>
        <StyledRange
          style={{
            left: `${((localValue[0] - min) / (max - min)) * 100}%`,
            width: `${((localValue[1] - localValue[0]) / (max - min)) * 100}%`,
          }}
        />
      </StyledTrack>

      <StyledThumb
        onMouseDown={() => startDrag(0)}
        onTouchStart={(e) => {
          if (e.cancelable) e.preventDefault();
          startDrag(0);
        }}
        style={{
          left: `${((localValue[0] - min) / (max - min)) * 100}%`,
        }}
      />
      <StyledThumb
        onMouseDown={() => startDrag(1)}
        onTouchStart={(e) => {
          if (e.cancelable) e.preventDefault();
          startDrag(1);
        }}
        style={{
          left: `${((localValue[1] - min) / (max - min)) * 100}%`,
        }}
      />
    </RangeSliderWrapper>
  );
};
